import React, {useContext, useEffect, useState} from "react";
import SmartTable from "../../../../components/SmartTable/allocation";
import OverviewTableContainer from "./OverviewTable";
import OverviewPanel from "../../../../views/DashboardView/SustainabilityView/AllocationView/MultiStepView/components/OverviewPanel";
import {Typography} from "@material-ui/core";
import {getStepData} from "../../../../api/sustainability/allocation/offRoadUse/stepData";
import { ReactReduxContext, useDispatch, useSelector } from "react-redux";
import parseAndFormat from "../../../../components/texts/parseAndFormat";
import parseToFloat from "../../../../components/texts/parseToFloat";
import {useAllocationEditTableStyles} from "../../../../common/styles/allocationStyles";
import {
  allocationTableHeadings,
  allocationPrevTableHeadings,
  allocationSurplusTableHeadings
} from "../shared/tableHeadings";
import { getBalancedData, getMappedData, isNonEmptyArrayOrObject } from "../shared/dataUtils";

const useStyles = useAllocationEditTableStyles;

interface UpdatedDataProps {
  biotemplates?: any[];
  overview?: any[];
  id?: number;
}

interface Props {
  generalAllocationNotNeeded: boolean;
  reqByCustomerDisabled: boolean;
  outJointDisabled: boolean;
  surplusStepDisabled: boolean;
  aboveMandateAfterFulfillDisabled: boolean;
  notApplicableStepDisabled: boolean;
  data: any[];
  activeStep: number;
  onUpdateData: (updatedData: UpdatedDataProps, type: string) => void;
  onUpdateRequirement: (requirementData: Requirement) => void;
  updateIsFetching: (value: boolean) => void;
}

interface OverViewReqParamProps {
  general_mandate: number;
  general_req_vol: number;
  general_rel_vol: number;
  above_mandate_req_by_cust: number;
  fame_advanced: number;
  fame: number;
  hvo_hro_advanced: number;
  hvo_hro: number;
  above_mandate_after_fulfill: number;
  vol_not_applicable_to_mandate: number;
  out_joint_fulfillment: number;
  surplus_volumes: number;
}
interface OverViewParamProps {
  general_mandate: number;
  general_req_vol: number;
  general_rel_vol: number;
  above_mandate_req_by_cust: number;
  fame_advanced: number;
  fame: number;
  hvo_hro_advanced: number;
  hvo_hro: number;
  above_mandate_after_fulfill: number;
  vol_not_applicable_to_mandate: number;
  out_joint_fulfillment: number;
  surplus_volumes: number;
}
interface OverViewProps {
  req_vol: OverViewReqParamProps;
  joint_fulfill: OverViewParamProps;
  allocated_LE: OverViewParamProps;
  allocated_15: OverViewParamProps;
  offroad_surplus: OverViewParamProps;
  remaining_vol: OverViewParamProps;
}
interface Requirement {
  construction_volume_requirement: any;
  construction_reserved_req: any;
  joint_fulfillment_out: any;
  construction_additional_req: any;
  construction_na_req: any;
}

const createDefaultOverViewState = (): OverViewProps => ({
  req_vol: {
    general_mandate: 0,
    general_req_vol: 0,
    general_rel_vol: 0,
    above_mandate_req_by_cust: 0,
    fame_advanced: 0,
    fame: 0,
    hvo_hro_advanced: 0,
    hvo_hro: 0,
    above_mandate_after_fulfill: 0,
    vol_not_applicable_to_mandate: 0,
    out_joint_fulfillment: 0,
    surplus_volumes: 0
  },
  joint_fulfill: {
    general_mandate: 0,
    general_req_vol: 0,
    general_rel_vol: 0,
    above_mandate_req_by_cust: 0,
    fame_advanced: 0,
    fame: 0,
    hvo_hro_advanced: 0,
    hvo_hro: 0,
    above_mandate_after_fulfill: 0,
    vol_not_applicable_to_mandate: 0,
    out_joint_fulfillment: 0,
    surplus_volumes: 0
  },
  allocated_LE: {
    general_mandate: 0,
    general_req_vol: 0,
    general_rel_vol: 0,
    above_mandate_req_by_cust: 0,
    fame_advanced: 0,
    fame: 0,
    hvo_hro_advanced: 0,
    hvo_hro: 0,
    above_mandate_after_fulfill: 0,
    vol_not_applicable_to_mandate: 0,
    out_joint_fulfillment: 0,
    surplus_volumes: 0
  },
  allocated_15: {
    general_mandate: 0,
    general_req_vol: 0,
    general_rel_vol: 0,
    above_mandate_req_by_cust: 0,
    fame_advanced: 0,
    fame: 0,
    hvo_hro_advanced: 0,
    hvo_hro: 0,
    above_mandate_after_fulfill: 0,
    vol_not_applicable_to_mandate: 0,
    out_joint_fulfillment: 0,
    surplus_volumes: 0
  },
  offroad_surplus: {
    general_mandate: 0,
    general_req_vol: 0,
    general_rel_vol: 0,
    above_mandate_req_by_cust: 0,
    fame_advanced: 0,
    fame: 0,
    hvo_hro_advanced: 0,
    hvo_hro: 0,
    above_mandate_after_fulfill: 0,
    vol_not_applicable_to_mandate: 0,
    out_joint_fulfillment: 0,
    surplus_volumes: 0
  },
  remaining_vol: {
    general_mandate: 0,
    general_req_vol: 0,
    general_rel_vol: 0,
    above_mandate_req_by_cust: 0,
    fame_advanced: 0,
    fame: 0,
    hvo_hro_advanced: 0,
    hvo_hro: 0,
    above_mandate_after_fulfill: 0,
    vol_not_applicable_to_mandate: 0,
    out_joint_fulfillment: 0,
    surplus_volumes: 0
  }
});

const EditableTable: React.FC<Props> = ({
                                          data,
                                          generalAllocationNotNeeded,
                                          reqByCustomerDisabled,
                                          outJointDisabled,
                                          surplusStepDisabled,
                                          aboveMandateAfterFulfillDisabled,
                                          notApplicableStepDisabled,
                                          activeStep,
                                          onUpdateData,
                                          onUpdateRequirement,
                                          updateIsFetching,
                                        }) => {
  const classes = useStyles();
  const [overViewData, setOverViewData] = useState<OverViewProps>(createDefaultOverViewState());
  const [responseOverViewData, setResponseOverViewData] = useState<OverViewProps>(createDefaultOverViewState());
  const [newData, setNewData] = useState([]);
  const [stepData, setStepData] = useState([]);
  const [stepDataId, setStepDataId] = useState<number|null>(null);
  const [stepTwosData, setStepTwosData] = useState([]);
  const [stepSurplusData, setStepSurplusData] = useState([]);
  const [stepThreesData, setStepThreesData] = useState([]);
  const [stepJointFulfsData, setStepJointFulfsData] = useState([]);
  const [stepFoursData, setStepFoursData] = useState([]);
  const [stepFivesData, setStepFivesData] = useState([]);
  const dispatch = useDispatch();
  const updateReqVol = (newValues: Partial<OverViewReqParamProps>) => {
    setOverViewData(prevData => ({
      ...prevData,
      req_vol: { ...prevData.req_vol, ...newValues },
    }));
  };
  const offroadAllocatedQuantityByStep = useContext(ReactReduxContext)
      .store.getState()
      .sustainability
      .offRoadAllocation;

  const updateJointFulfillVol = (newValues: Partial<OverViewParamProps>) => {
    setOverViewData(prevData => ({
      ...prevData,
      joint_fulfill: { ...prevData.joint_fulfill, ...newValues },
    }));
  };

  const updateAllocatedLEVol = (newValues: Partial<OverViewParamProps>) => {
    setOverViewData(prevData => ({
      ...prevData,
      allocated_LE: { ...prevData.allocated_LE, ...newValues },
    }));
  };

  const updateAllocated15Vol = (newValues: Partial<OverViewParamProps>) => {
    setOverViewData(prevData => ({
      ...prevData,
      allocated_15: { ...prevData.allocated_15, ...newValues },
    }));
  };

  const updateSurplusFromOffroadVol = (newValues: Partial<OverViewParamProps>) => {
    setOverViewData(prevData => ({
      ...prevData,
      offroad_surplus: { ...prevData.offroad_surplus, ...newValues },
    }));
  };

  const getAllocated = (row: any) => parseAndFormat(row.allocated_quantity, 2);

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>, row: any) => {
    const { value } = event.target;
    const prevData = { ...row };
    const inputValue = value.replace(/[^0-9.,]/g, "").replace(/,/g, ".");
    const formattedAllocated = inputValue === "" ? 0 : parseAndFormat(inputValue, 0, true);
    const newData = { ...row, allocated_quantity: formattedAllocated };
    // Assuming you are updating some state here
    setNewData(prevRows => {
      const updatedRowsCopy = [...prevRows];
      // @ts-ignore
      const rowIndex = updatedRowsCopy.findIndex(r => r.id === row.id);

      if (rowIndex !== -1) {
        // @ts-ignore
        updatedRowsCopy[rowIndex] = newData;
      }

      return updatedRowsCopy;
    });
  };

  const handleOnBlur = (event: React.ChangeEvent<HTMLInputElement>, row: any[]) => {
    handleOnChange(event, row);
  };

  const getData = (data: any[], activeStep: number) => {
      return data;
  };

  const fetchBiotemplateData = async (activeStep: number, currentStepData: any[]) => {
    const response = await getStepData(activeStep);
    if (response) {
      if ("data" in response) {
        setStepData(response.data);
        if ("id" in response.data) {
          setStepDataId(response.data.id);
        }

        if ("req_vol" in response.data) {
          updateReqVol(response.data.req_vol);
          setResponseOverViewData(prevData => ({
            ...prevData,
            req_vol: { ...prevData.req_vol, ...response.data.req_vol },
          }));
        }
        if ("joint_fulfill" in response.data) {
          updateJointFulfillVol(response.data.joint_fulfill);
          setResponseOverViewData(prevData => ({
            ...prevData,
            joint_fulfill: { ...prevData.joint_fulfill, ...response.data.joint_fulfill },
          }));
        }
        if ("allocated_LE" in response.data) {
          updateAllocatedLEVol(response.data.allocated_LE);
          setResponseOverViewData(prevData => ({
            ...prevData,
            allocated_LE: { ...prevData.allocated_LE, ...response.data.allocated_LE },
          }));
        }
        if ("allocated_15" in response.data) {
          updateAllocated15Vol(response.data.allocated_15);
          setResponseOverViewData(prevData => ({
            ...prevData,
            allocated_15: { ...prevData.allocated_15, ...response.data.allocated_15 },
          }));
        }
        if ("offroad_surplus" in response.data) {
          updateSurplusFromOffroadVol(response.data.offroad_surplus);
          setResponseOverViewData(prevData => ({
            ...prevData,
            offroad_surplus: { ...prevData.offroad_surplus, ...response.data.offroad_surplus },
          }));
        }

        if ( "table_from_step_surplus_volume" in response.data &&
          isNonEmptyArrayOrObject(response.data.table_from_step_surplus_volume)) {
          if (Array.isArray(response.data.table_from_step_surplus_volume)) {
            setStepSurplusData(response.data.table_from_step_surplus_volume);
          } else {
            setStepSurplusData(Object.values(response.data.table_from_step_surplus_volume));
          }
        }

        if ( "table_from_step_two" in response.data && isNonEmptyArrayOrObject(response.data.table_from_step_two)) {
          if (Array.isArray(response.data.table_from_step_two)) {
            setStepTwosData(response.data.table_from_step_two);
          } else {
            setStepTwosData(Object.values(response.data.table_from_step_two));
          }
        }

        if ( "table_from_step_three" in response.data && isNonEmptyArrayOrObject(response.data.table_from_step_three)) {
          if (Array.isArray(response.data.table_from_step_three)) {
            setStepThreesData(response.data.table_from_step_three);
          } else {
            setStepThreesData(Object.values(response.data.table_from_step_three));
          }
        }

        if ( "table_from_step_joint_fulf" in response.data &&
            isNonEmptyArrayOrObject(response.data.table_from_step_joint_fulf) &&
            !outJointDisabled
        ) {
          if (Array.isArray(response.data.table_from_step_joint_fulf)) {
            setStepJointFulfsData(response.data.table_from_step_joint_fulf);
          } else {
            setStepJointFulfsData(Object.values(response.data.table_from_step_joint_fulf));
          }
        }

        if ( "table_from_step_four" in response.data && isNonEmptyArrayOrObject(response.data.table_from_step_four)) {
          if (Array.isArray(response.data.table_from_step_four)) {
            setStepFoursData(response.data.table_from_step_four);
          } else {
            setStepFoursData(Object.values(response.data.table_from_step_four));
          }
        }

        if ( "table_from_step_five" in response.data && isNonEmptyArrayOrObject(response.data.table_from_step_five)) {
          if (Array.isArray(response.data.table_from_step_five)) {
            setStepFivesData(response.data.table_from_step_five);
          } else {
            setStepFivesData(Object.values(response.data.table_from_step_five));
          }
        }

        let mappedData;

        if (activeStep === 1 && "table_from_step_surplus_volume" in response.data && !surplusStepDisabled) {
          // @ts-ignore
          mappedData = getMappedData(currentStepData, response.data.table_from_step_surplus_volume);
        } else if (activeStep === 2 && "table_from_step_two" in response.data) {
          // @ts-ignore
          mappedData = getMappedData(currentStepData, response.data.table_from_step_two);
        } else if (activeStep === 3 && "table_from_step_three" in response.data) {
          // @ts-ignore
          mappedData = getMappedData(currentStepData, response.data.table_from_step_three);
        } else if (activeStep === 4 && "table_from_step_joint_fulf" in response.data && !outJointDisabled) {
          // @ts-ignore
          mappedData = getMappedData(currentStepData, response.data.table_from_step_joint_fulf);
        } else if (activeStep === 5 && "table_from_step_four" in response.data) {
          // @ts-ignore
          mappedData = getMappedData(currentStepData, response.data.table_from_step_four);
        } else if (activeStep === 6 && "table_from_step_five" in response.data && !notApplicableStepDisabled) {
          // @ts-ignore
          mappedData = getMappedData(currentStepData, response.data.table_from_step_five);
        } else {
          mappedData = currentStepData;
        }
        const calculatedData = await calculateMappedData(activeStep, mappedData);

        // @ts-ignore
        setNewData(calculatedData);
      } else {
        setStepData([]);
      }
    } else {
      alert("Some error occured");
    }
  };

  const calculateMappedData = async (activeStep: number, data: any[]) => {
    let newCalculatedData = data;

    // Define the steps and the corresponding allocation data
    const steps = [
      offroadAllocatedQuantityByStep.biotemplatesInAllocationStepSurplusVol,
      offroadAllocatedQuantityByStep.biotemplatesInAllocationStepOne,
      offroadAllocatedQuantityByStep.biotemplatesInAllocationStepTwo,
      offroadAllocatedQuantityByStep.biotemplatesInAllocationStepJointFulf,
      offroadAllocatedQuantityByStep.biotemplatesInAllocationStepThree,
      offroadAllocatedQuantityByStep.biotemplatesInAllocationStepFour
    ];

    // Filter out the current active step
    const filteredSteps = steps.filter((_, index) => index !== activeStep - 1 && index > 0);

    // Apply the balance operation to each filtered step
    // for (const stepData of filteredSteps) {
    //   if (stepData) {
    //     newCalculatedData = await getBalancedData(newCalculatedData, stepData);
    //   }
    // }

    // for (const item of newCalculatedData) {
    //     if (parseToFloat(item.allocated_quantity) > 0) {
    //         item.available_quantity = parseToFloat(item.available_quantity) + parseToFloat(item.allocated_quantity);
    //     }
    // }

    newCalculatedData = newCalculatedData.filter(item => item.available_quantity >= 0);

    return newCalculatedData;
  };

  const updateDataTotals = (responseOverViewData: OverViewProps, newData: any[]) => {

    const dataTotals: OverViewProps = JSON.parse(JSON.stringify(responseOverViewData));

    const in_joint_fulfill_general = parseToFloat(dataTotals.joint_fulfill.general_mandate.toString(), 2);
    const in_joint_fulfill_not_applicable = parseToFloat(dataTotals.joint_fulfill.vol_not_applicable_to_mandate.toString(), 2);

    let surplus_general_mandate = activeStep == 1 ? 0 : parseToFloat(dataTotals.offroad_surplus.general_mandate.toString());

    let allocated_15_general_sub_mandate = activeStep < 3 ? 0 : parseToFloat(dataTotals.allocated_15.general_mandate.toString());

    const above_mandate_req_by_cust =  activeStep < 3 ? 0 : parseToFloat(dataTotals.req_vol.above_mandate_req_by_cust.toString());
    let allocated_15_above_mandate_req_by_cust = activeStep == 3 ? 0 : parseToFloat(dataTotals.allocated_15.above_mandate_req_by_cust.toString());
    let allocated_LE_above_mandate_req_by_cust = activeStep == 3 ? 0 : parseToFloat(dataTotals.allocated_LE.above_mandate_req_by_cust.toString());

    const above_mandate_after_fulfill_req_vol = activeStep < 3 ? 0 : parseToFloat(dataTotals.req_vol.above_mandate_after_fulfill.toString());
    let allocated_15_above_mandate_after_fulfill = activeStep == 5 ? 0 : parseToFloat(dataTotals.allocated_15.above_mandate_after_fulfill.toString());
    let allocated_LE_above_mandate_after_fulfill = activeStep == 5 ? 0 : parseToFloat(dataTotals.allocated_LE.above_mandate_req_by_cust.toString());

    const vol_not_applicable_to_mandate = activeStep < 4 ? 0 : parseToFloat(dataTotals.req_vol.vol_not_applicable_to_mandate.toString());
    let allocated_15_vol_not_applicable_to_mandate = activeStep < 5 ? 0 : parseToFloat(dataTotals.allocated_15.vol_not_applicable_to_mandate.toString());
    let allocated_LE_vol_not_applicable_to_mandate = activeStep < 5 ? 0 : parseToFloat(dataTotals.allocated_LE.vol_not_applicable_to_mandate.toString());

    const out_joint_fulfillment_req_vol = outJointDisabled ? 0 : parseToFloat(dataTotals.req_vol.out_joint_fulfillment.toString());
    let allocated_15_out_joint_fulfillment = (activeStep == 4 && !outJointDisabled) ? 0 : parseToFloat(dataTotals.allocated_15.out_joint_fulfillment.toString());
    let allocated_LE_out_joint_fulfillment = (activeStep == 4 && !outJointDisabled) ? 0 : parseToFloat(dataTotals.allocated_LE.out_joint_fulfillment.toString());
    if (activeStep !== 4 && outJointDisabled) {
        allocated_15_out_joint_fulfillment = 0;
        allocated_LE_out_joint_fulfillment = 0;
    }

    const fame = parseToFloat(dataTotals.req_vol.fame.toString());
    const allocated_LE_fame = 0;
    let allocated_15_fame = 0;

    const fame_advanced = parseToFloat(dataTotals.req_vol.fame_advanced.toString());
    const allocated_LE_fame_advanced = 0;
    let allocated_15_fame_advanced = 0;

    const hvo_hro = parseToFloat(dataTotals.req_vol.hvo_hro.toString());
    const allocated_LE_hvo_hro = 0;
    let allocated_15_hvo_hro = 0;

    const hvo_hro_advanced = parseToFloat(dataTotals.req_vol.hvo_hro_advanced.toString());
    const allocated_LE_hvo_hro_advanced = 0;
    let allocated_15_hvo_hro_advanced = 0;

    for (const row of newData) {
      if (activeStep === 1 && !surplusStepDisabled) {
        // @ts-ignore
        surplus_general_mandate += parseToFloat(row.allocated_quantity);
      }

      if (activeStep === 2) {
        // @ts-ignore
        allocated_15_general_sub_mandate += parseToFloat(row.allocated_quantity);
      }
      if (activeStep === 3) {
        // @ts-ignore
        allocated_LE_above_mandate_req_by_cust += parseToFloat(row.allocated_quantity);
        // @ts-ignore
        allocated_15_above_mandate_req_by_cust += parseToFloat(row.allocated_quantity);
      }

      if (activeStep === 4 && !outJointDisabled) {
        // @ts-ignore
        allocated_LE_out_joint_fulfillment += parseToFloat(row.allocated_quantity);
        // @ts-ignore
        allocated_15_out_joint_fulfillment += parseToFloat(row.allocated_quantity);
      }

      if (activeStep === 5) {
        // @ts-ignore
        allocated_LE_above_mandate_after_fulfill += parseToFloat(row.allocated_quantity);
        // @ts-ignore
        allocated_15_above_mandate_after_fulfill += parseToFloat(row.allocated_quantity);
      }

      if (activeStep === 6 && !notApplicableStepDisabled) {
        // @ts-ignore
        allocated_LE_vol_not_applicable_to_mandate += parseToFloat(row.allocated_quantity);
        // @ts-ignore
        allocated_15_vol_not_applicable_to_mandate += parseToFloat(row.allocated_quantity);
      }
      // @ts-ignore
      if (row.double_counted_norway == "Ja" && row.biofuel_type == "FAME") {
        // @ts-ignore
        // allocated_LE_fame_advanced += parseFloat(row.allocated_quantity);
        // @ts-ignore
        allocated_15_fame_advanced += parseToFloat(row.allocated_quantity);
      }

      // @ts-ignore
      if (row.double_counted_norway == "Ja" && row.biofuel_type == "HVO/HRO") {
        // @ts-ignore
        allocated_15_hvo_hro_advanced += parseToFloat(row.allocated_quantity);
      }

      // @ts-ignore
      if (row.double_counted_norway == "Nei" && row.biofuel_type == "HVO/HRO") {
        // @ts-ignore
        // allocated_LE_hvo_hro += parseFloat(row.allocated_quantity);
        // // @ts-ignore
         allocated_15_hvo_hro += parseToFloat(row.allocated_quantity);
      }

      // @ts-ignore
      if (row.double_counted_norway == "Nei" && row.biofuel_type == "FAME") {
        // @ts-ignore
        // allocated_LE_fame += parseFloat(row.allocated_quantity);
        // @ts-ignore
        allocated_15_fame += parseToFloat(row.allocated_quantity);
      }

    }
    dataTotals.req_vol.general_req_vol = parseToFloat(dataTotals.req_vol.general_req_vol.toString(), 2);
    dataTotals.req_vol.above_mandate_req_by_cust = parseToFloat(dataTotals.req_vol.above_mandate_req_by_cust.toString(), 2);
    dataTotals.req_vol.above_mandate_after_fulfill = parseToFloat(dataTotals.req_vol.above_mandate_after_fulfill.toString(), 2);
    dataTotals.req_vol.vol_not_applicable_to_mandate = parseToFloat(dataTotals.req_vol.vol_not_applicable_to_mandate.toString(), 2);

    dataTotals.allocated_15.general_mandate = allocated_15_general_sub_mandate;

    dataTotals.offroad_surplus.general_mandate = surplus_general_mandate;

    dataTotals.remaining_vol.general_mandate =   dataTotals.req_vol.general_req_vol -
        dataTotals.allocated_15.general_mandate - dataTotals.offroad_surplus.general_mandate - in_joint_fulfill_general;

    dataTotals.allocated_15.above_mandate_req_by_cust = allocated_15_above_mandate_req_by_cust;
    dataTotals.remaining_vol.above_mandate_req_by_cust = dataTotals.req_vol.above_mandate_req_by_cust - allocated_15_above_mandate_req_by_cust;

    dataTotals.allocated_15.above_mandate_after_fulfill = allocated_15_above_mandate_after_fulfill;
    dataTotals.remaining_vol.above_mandate_after_fulfill = dataTotals.req_vol.above_mandate_after_fulfill - allocated_15_above_mandate_after_fulfill;

    dataTotals.allocated_15.vol_not_applicable_to_mandate = allocated_15_vol_not_applicable_to_mandate;
    dataTotals.remaining_vol.vol_not_applicable_to_mandate = dataTotals.req_vol.vol_not_applicable_to_mandate - allocated_15_vol_not_applicable_to_mandate - in_joint_fulfill_not_applicable;

    dataTotals.allocated_LE.out_joint_fulfillment = allocated_LE_out_joint_fulfillment;
    dataTotals.allocated_15.out_joint_fulfillment = allocated_15_out_joint_fulfillment;
    dataTotals.remaining_vol.out_joint_fulfillment = out_joint_fulfillment_req_vol - allocated_15_out_joint_fulfillment;

    dataTotals.allocated_15.hvo_hro_advanced = allocated_15_hvo_hro_advanced;
    dataTotals.remaining_vol.hvo_hro_advanced = dataTotals.req_vol.hvo_hro_advanced - dataTotals.allocated_15.hvo_hro_advanced;

    dataTotals.allocated_15.fame_advanced = allocated_15_fame_advanced;
    dataTotals.remaining_vol.fame_advanced = dataTotals.req_vol.fame_advanced - dataTotals.allocated_15.fame_advanced;

    dataTotals.allocated_15.hvo_hro = allocated_15_hvo_hro;
    dataTotals.remaining_vol.hvo_hro = dataTotals.req_vol.hvo_hro - dataTotals.allocated_15.hvo_hro;

    dataTotals.allocated_15.fame = allocated_15_fame;
    dataTotals.remaining_vol.fame = dataTotals.req_vol.fame - dataTotals.allocated_15.fame;

    return dataTotals;
  };

  useEffect(() => {
    // let newDataForStep;

    // switch (activeStep) {
    //   default:
    //     newDataForStep = removeUnavailableBiotemplates(data);
    //     break;
    // }
    // setNewData(newDataForStep);
    // @ts-ignore
    setNewData(data);
  }, [data, activeStep]);

  useEffect(() => {
    updateIsFetching(true);
    // let currentStepData;

    // switch (activeStep) {
    //   default:
    //     currentStepData = removeUnavailableBiotemplates(data);
    //     break;
    // }

    // fetchBiotemplateData(activeStep, currentStepData);
    fetchBiotemplateData(activeStep, data);
    updateIsFetching(false);
  }, [activeStep]);

  useEffect(() => {
    // @ts-ignore
    const copy = newData.map(item => ({ ...item,  allocated_quantity: parseToFloat(item.allocated_quantity)}));

    // @ts-ignore
    const newArray = {
      // @ts-ignore
      biotemplates: copy.filter(item => item.allocated_quantity > 0).map(item => ({id: item.id, available_quantity: item.available_quantity, allocated_quantity: item.allocated_quantity}))
    };
    // @ts-ignore
    onUpdateData(newArray, "bio");

    if (activeStep > 1 && activeStep <= 6) {
      const params: Requirement = {
        construction_volume_requirement: {},
        construction_reserved_req: {},
        joint_fulfillment_out: {},
        construction_additional_req: {},
        construction_na_req: {}
      };
      console.log("activeStep", activeStep);

      stepTwosData.forEach(item => {
        // @ts-ignore
        params.construction_volume_requirement[item.biotemplate_id] = parseToFloat(item.allocated_quantity);
      });

      stepThreesData.forEach(item => {
        // @ts-ignore
        params.construction_reserved_req[item.biotemplate_id] = parseToFloat(item.allocated_quantity);
      });

      if (!outJointDisabled) {
        stepJointFulfsData.forEach(item => {
          // @ts-ignore
          params.joint_fulfillment_out[item.biotemplate_id] = parseToFloat(item.allocated_quantity);
        });
      }

      stepFoursData.forEach(item => {
        // @ts-ignore
        params.construction_additional_req[item.biotemplate_id] = parseToFloat(item.allocated_quantity);
      });

      stepFivesData.forEach(item => {
        // @ts-ignore
        params.construction_na_req[item.biotemplate_id] = parseToFloat(item.allocated_quantity);
      });

      const updatedArray = { biotemplates: [] };

      // Create a map for faster lookup
      const newArrayMap = {};
      newArray.biotemplates.forEach(item => {
        newArrayMap[item.id] = item.allocated_quantity;
      });

      // Update newArray based on stepFivesData

      // Add the remaining entries from newArrayMap to updatedArray
      Object.entries(newArrayMap).forEach(([id, allocated_quantity]) => {
        // @ts-ignore
        updatedArray.biotemplates.push({ id: Number(id), allocated_quantity: parseToFloat(String(allocated_quantity)) });
      });

        if (activeStep == 2) {
          updatedArray.biotemplates.forEach(item => {
            // @ts-ignore
            params.construction_volume_requirement[item.id] = parseToFloat(item.allocated_quantity);
          });
        }
        if (activeStep == 3) {
          updatedArray.biotemplates.forEach(item => {
            // @ts-ignore
            params.construction_reserved_req[item.id] = parseToFloat(item.allocated_quantity);
          });
        }
        if (activeStep == 4 && !outJointDisabled) {
          updatedArray.biotemplates.forEach(item => {
            // @ts-ignore
            params.joint_fulfillment_out[item.id] = parseToFloat(item.allocated_quantity);
          });
        }
        if (activeStep == 5) {
          updatedArray.biotemplates.forEach(item => {
            // @ts-ignore
            params.construction_additional_req[item.id] = parseToFloat(item.allocated_quantity);
          });
        }

        if (activeStep ==6) {
          updatedArray.biotemplates.forEach(item => {
            // @ts-ignore
            params.construction_na_req[item.id] = parseToFloat(item.allocated_quantity);
          });
        }

        console.log("params after", params);

      // @ts-ignore
      onUpdateRequirement(params);
    }

  }, [activeStep, newData]);

  useEffect(() => {

    const dataTotals: OverViewProps = updateDataTotals(responseOverViewData, newData);

    const newArray = {
      // @ts-ignore
      overview: dataTotals,
      id: stepDataId
    };

    setOverViewData(dataTotals);
    // @ts-ignore
    onUpdateData(newArray, "overview");
  }, [newData]);

  return (
      <>
        <div className={classes.tableContainer}>
          {(activeStep != 4 || (activeStep === 4 && !outJointDisabled)) &&
              (activeStep != 5 || (activeStep === 5 && !aboveMandateAfterFulfillDisabled)) &&
              (activeStep != 6 || (activeStep === 6 && !notApplicableStepDisabled)) && (
              <>
                {activeStep > 2 && (
                    <>
                      <Typography variant="h4">Overview of allocated volumes from Step 2 (general mandate)</Typography>
                      <br/>
                      <SmartTable
                          headCells={allocationPrevTableHeadings}
                          rows={stepTwosData}
                          verticalOverflow={true}
                          whiteHeader={true}
                      /><br/><br/>
                    </>
                )}

                {activeStep > 3 && !reqByCustomerDisabled && (
                    <>
                      <Typography variant="h4">
                        Overview of allocated volumes from Step 3 (above the mandate required by customers)
                      </Typography><br/>
                      <SmartTable
                          headCells={allocationPrevTableHeadings}
                          rows={stepThreesData}
                          verticalOverflow={true}
                          whiteHeader={true}
                      /><br/><br/>
                    </>
                )}
              </>
          )}
          {activeStep > 4 && !outJointDisabled &&
              (activeStep != 5 || (activeStep === 5 && !aboveMandateAfterFulfillDisabled)) &&
              (activeStep != 6 || (activeStep === 6 && !notApplicableStepDisabled)) && (
              <>
                <Typography variant="h4">Overview of allocated volumes from Step 4</Typography><br/>
                <SmartTable
                    headCells={allocationPrevTableHeadings}
                    rows={stepJointFulfsData}
                    verticalOverflow={true}
                    whiteHeader={true}
                /><br/><br/>
              </>
          )}

          {activeStep > 5 && !aboveMandateAfterFulfillDisabled &&
              (activeStep != 6 || (activeStep === 6 && !notApplicableStepDisabled)) && (
              <>
                <Typography variant="h4">Overview of allocated volumes from Step 5</Typography><br/>
                <SmartTable
                    headCells={allocationPrevTableHeadings}
                    rows={stepFoursData}
                    verticalOverflow={true}
                    whiteHeader={true}
                /><br/><br/>
              </>
          )}
          {activeStep > 6 && !notApplicableStepDisabled && (
              <>
                <Typography variant="h4">Overview of allocated volumes from Step 6</Typography><br/>
                <SmartTable
                    headCells={allocationPrevTableHeadings}
                    rows={stepFivesData}
                    verticalOverflow={true}
                    whiteHeader={true}
                /><br/><br/>
              </>
          )}
          {activeStep === 1 ? (
            <>
            {!surplusStepDisabled && (
              <>
                <Typography variant="h4">Allocation Table</Typography><br/>
                <SmartTable
                    headCells={allocationSurplusTableHeadings}
                    rows={getData(newData, activeStep)}
                    editableProps={{
                      id: "allocated_quantity",
                      getValue: getAllocated,
                      onChange: handleOnChange,
                      onBlur: handleOnBlur,
                    }}
                    verticalOverflow={true}
                    whiteHeader={true}
                />
              </>
            )}
            </>
          ) : (
            <>
              {(activeStep != 4 || (activeStep === 4 && !outJointDisabled)) &&
                  (activeStep != 6 || (activeStep === 6 && !notApplicableStepDisabled)) &&
                  (activeStep != 3 || (activeStep === 3 && !reqByCustomerDisabled)) &&
                  (activeStep != 2 || (activeStep === 2 && !generalAllocationNotNeeded)) &&
                (activeStep != 5 || (activeStep === 5 && !aboveMandateAfterFulfillDisabled)) && (
                <>
                  <Typography variant="h4">Allocation Table</Typography><br/>
                  <SmartTable
                      headCells={allocationTableHeadings}
                      rows={getData(newData, activeStep)}
                      editableProps={{
                        id: "allocated_quantity",
                        getValue: getAllocated,
                        onChange: handleOnChange,
                        onBlur: handleOnBlur,
                      }}
                      verticalOverflow={true}
                      whiteHeader={true}
                  />
                </>
              )}
            </>
          )}
        </div>
        {(activeStep != 4 || (activeStep === 4 && !outJointDisabled)) &&
            (activeStep != 6 || (activeStep === 6 && !notApplicableStepDisabled)) &&
            (activeStep != 3 || (activeStep === 3 && !reqByCustomerDisabled)) &&
            (activeStep != 1 || (activeStep === 1 &&  !surplusStepDisabled)) &&
            (activeStep != 5 || (activeStep === 5 && !aboveMandateAfterFulfillDisabled)) && (
            <OverviewPanel className={classes.overviewPanel}>
              <OverviewTableContainer data={overViewData} step={activeStep}/>
            </OverviewPanel>
        )}
      </>
  );
};

// @ts-ignore
export default EditableTable;
